import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, HelpCircle } from "lucide-react";
import { useApp } from "../context/AppContext";

const DEFAULT_FAQS_TR = [
    { id: "d1", question: "Rezervasyon nasıl kesinleşir?", answer: "Talebinizi ilettikten sonra ekibimiz sizinle iletişime geçer, müsaitlik teyit edilir ve rezervasyonunuz onaylanarak kesinleşir." },
    { id: "d2", question: "İptal ve değişiklik politikası nedir?", answer: "Giriş tarihinden 14 gün önce yapılan iptallerde ön ödemenin tamamı iade edilir. 7 gün içindeki iptallerde tutar bir sonraki rezervasyonunuzda kullanılabilir." },
    { id: "d3", question: "Tur şirketleri için özel fiyat var mı?", answer: "Evet. Tur şirketleri için toplu rezervasyonlarda özel indirimli fiyatlar sunuyoruz. Detaylı teklif için WhatsApp üzerinden bizimle iletişime geçin." },
    { id: "d4", question: "Yemek seçenekleri nelerdir?", answer: "Kahvaltı, yarım pansiyon ve tam pansiyon seçenekleri her otelimizde mevcuttur. Yemekler kişi başına ve gece başına ücretlendirilir." },
    { id: "d5", question: "Çocuklar için ücret farkı uygulanıyor mu?", answer: "0-6 yaş arası çocuklar ebeveyni ile aynı yatakta konaklarsa ücretsizdir. 7 yaş ve üzeri tam ücret üzerinden hesaplanır." },
];

const DEFAULT_FAQS_EN = [
    { id: "d1", question: "How is a reservation confirmed?", answer: "After you send your request, our team contacts you, availability is confirmed and your reservation is finalized upon approval." },
    { id: "d2", question: "What is the cancellation & change policy?", answer: "Cancellations made 14+ days before check-in receive a full deposit refund. Within 7 days, the amount can be applied to a future reservation." },
    { id: "d3", question: "Are there special rates for tour operators?", answer: "Yes — we offer discounted group rates for tour companies. Contact us on WhatsApp for a tailored quote." },
    { id: "d4", question: "What meal plans are available?", answer: "Breakfast, half board and full board options are available at every hotel. Meals are priced per person per night." },
    { id: "d5", question: "Are there different rates for children?", answer: "Children aged 0-6 sharing the parent's bed stay free. Ages 7 and above are charged at full rate." },
];

const FAQSection = ({ items, title, subtitle }) => {
    const { t, lang } = useApp();
    const DEFAULT_FAQS = lang === "en" ? DEFAULT_FAQS_EN : DEFAULT_FAQS_TR;
    const list = items && items.length
        ? items.map((f) => (lang === "en"
            ? { ...f, question: f.question_en || f.question, answer: f.answer_en || f.answer }
            : f))
        : DEFAULT_FAQS;
    const [open, setOpen] = useState(null);
    const heading = title || t("home.faq_title");
    const sub = subtitle || t("home.faq_subtitle");

    return (
        <section className="py-20 lg:py-28 max-w-4xl mx-auto px-6 lg:px-10" data-testid="faq-section">
            <div className="text-center mb-12">
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center justify-center gap-2">
                    <HelpCircle className="w-3.5 h-3.5" /> {lang === "en" ? "Help" : "Yardım"}
                </div>
                <h2 className="font-serif text-4xl lg:text-5xl text-balance">{heading}</h2>
                <p className="mt-3 text-muted_green text-sm">{sub}</p>
            </div>
            <div className="space-y-2">
                {list.map((f, idx) => {
                    const isOpen = open === f.id;
                    return (
                        <motion.div
                            key={f.id}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.35, delay: idx * 0.04 }}
                            className="border border-border_green bg-emerald_surface"
                            data-testid={`faq-item-${f.id}`}
                        >
                            <button
                                onClick={() => setOpen(isOpen ? null : f.id)}
                                className="w-full flex items-center justify-between text-left px-5 py-4 hover:bg-emerald_hover transition-colors"
                                data-testid={`faq-toggle-${f.id}`}
                            >
                                <span className="font-serif text-lg pr-4">{f.question}</span>
                                <span className="text-gold flex-shrink-0">
                                    {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                </span>
                            </button>
                            <AnimatePresence initial={false}>
                                {isOpen && (
                                    <motion.div
                                        key="content"
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.25 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="px-5 pb-5 pt-1 text-sm leading-relaxed text-muted_green border-t border-border_green">
                                            {f.answer}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
};

export default FAQSection;
